import { useEffect, useMemo, useState } from 'react';

const ANIMATION_TIMEOUT_MS = 2000;
const QUERY_PARAM_NAME = 'waitForContentReady';

export const shouldEnableContentReadyTracking = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return params.has(QUERY_PARAM_NAME);
};

export const useContentReady = (
  enabled: boolean,
  contentKey: unknown = enabled,
  contentRendered = true
): boolean => {
  const trackedContent = useMemo(
    () => ({ key: contentKey }),
    [enabled, contentKey, contentRendered]
  );
  const [readyContent, setReadyContent] = useState<
    typeof trackedContent | null
  >(null);

  useEffect(() => {
    if (!enabled || !contentRendered) return;

    let cancelled = false;
    const checkReadiness = async () => {
      try {
        await Promise.all([
          checkImagesLoaded(),
          checkFontsLoaded(),
          checkAnimationsComplete()
        ]);
        if (!cancelled) setReadyContent(trackedContent);
      } catch (error) {
        console.error('Error checking content readiness:', error);
        if (!cancelled) setReadyContent(trackedContent);
      }
    };

    checkReadiness();
    return () => {
      cancelled = true;
    };
  }, [enabled, contentRendered, trackedContent]);

  return !enabled || (contentRendered && readyContent === trackedContent);
};

const checkImagesLoaded = (): Promise<void> => {
  return new Promise(resolve => {
    const checkForBackgroundImages = () => {
      const allImages = Array.from(document.querySelectorAll('img'));
      const visibleImages = allImages.filter(img => isElementVisible(img));
      const backgroundImageUrls = getBackgroundImageUrls();

      return { images: visibleImages, backgroundImageUrls };
    };

    const waitForBackgroundImages = () => {
      const { images, backgroundImageUrls } = checkForBackgroundImages();

      if (backgroundImageUrls.length > 0 || images.length > 0) {
        proceedWithImageLoad(images, backgroundImageUrls, resolve);
      } else {
        let checkInterval: NodeJS.Timeout | null = null;
        let timeoutTimer: NodeJS.Timeout | null = null;
        let hasMutations = false;

        const checkForImages = () => {
          const { images: newImages, backgroundImageUrls: newBgUrls } =
            checkForBackgroundImages();

          if (newBgUrls.length > 0 || newImages.length > 0) {
            observer.disconnect();
            if (checkInterval) clearInterval(checkInterval);
            if (timeoutTimer) clearTimeout(timeoutTimer);
            proceedWithImageLoad(newImages, newBgUrls, resolve);
          }
        };

        const observer = new MutationObserver(() => {
          if (!hasMutations) {
            hasMutations = true;
            checkInterval = setInterval(checkForImages, 200);
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class']
        });

        timeoutTimer = setTimeout(() => {
          observer.disconnect();
          if (checkInterval) clearInterval(checkInterval);
          resolve();
        }, 3000);
      }
    };

    waitForBackgroundImages();
  });
};

const proceedWithImageLoad = (
  images: HTMLImageElement[],
  backgroundImageUrls: string[],
  resolve: () => void
) => {
  const totalChecks = images.length + backgroundImageUrls.length;

  if (totalChecks === 0) {
    resolve();
    return;
  }

  let loadedCount = 0;

  const handleLoad = () => {
    loadedCount++;
    if (loadedCount === totalChecks) {
      resolve();
    }
  };

  // Check <img> tags
  const incompleteImages = images.filter(img => !img.complete);

  if (incompleteImages.length === 0 && backgroundImageUrls.length === 0) {
    resolve();
    return;
  }

  // Already loaded images
  loadedCount += images.length - incompleteImages.length;

  // Attach listeners to incomplete <img> tags
  incompleteImages.forEach(img => {
    img.addEventListener('load', handleLoad, { once: true });
    img.addEventListener('error', handleLoad, { once: true });
  });

  // Check CSS background images
  backgroundImageUrls.forEach(url => {
    const img = new Image();
    img.onload = handleLoad;
    img.onerror = handleLoad;
    img.src = url;
  });

  // If all images were already loaded
  if (loadedCount === totalChecks) {
    resolve();
  }
};

const isElementVisible = (element: Element): boolean => {
  const style = window.getComputedStyle(element);

  // Check element styles
  if (style.display === 'none') {
    return false;
  }
  if (style.visibility === 'hidden') {
    return false;
  }
  if (style.opacity === '0') {
    return false;
  }

  // Check dimensions
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    // Exception for images that are loading - they might have zero height until loaded
    if (element instanceof HTMLImageElement) {
      const hasNaturalDimensions =
        element.naturalWidth > 0 || element.naturalHeight > 0;
      const isLoading = !element.complete;

      if (hasNaturalDimensions || isLoading) {
        return true; // Image is loading, will have dimensions once loaded
      }
    }

    return false;
  }

  // Check if positioned off-screen
  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;

  if (
    rect.right < 0 ||
    rect.bottom < 0 ||
    rect.left > viewportWidth ||
    rect.top > viewportHeight
  ) {
    return false;
  }

  // Check if any parent is hidden
  let parent = element.parentElement;
  while (parent) {
    const parentStyle = window.getComputedStyle(parent);
    if (parentStyle.display === 'none') {
      return false;
    }
    if (parentStyle.visibility === 'hidden') {
      return false;
    }
    if (parentStyle.opacity === '0') {
      return false;
    }
    parent = parent.parentElement;
  }

  return true;
};

const getBackgroundImageUrls = (): string[] => {
  const urls: string[] = [];
  const elements = document.querySelectorAll('*');

  elements.forEach(element => {
    const style = window.getComputedStyle(element);
    const bgImage = style.backgroundImage;

    if (bgImage && bgImage !== 'none') {
      if (!isElementVisible(element)) {
        return;
      }

      // Extract URL from background-image: url("...")
      const matches = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/g);
      if (matches) {
        matches.forEach(match => {
          const url = match.replace(/url\(['"]?/, '').replace(/['"]?\)/, '');
          if (url && !url.startsWith('data:')) {
            urls.push(url);
          }
        });
      }
    }
  });

  return Array.from(new Set(urls));
};

const checkFontsLoaded = (): Promise<void> => {
  if (!document.fonts || !document.fonts.ready) {
    return Promise.resolve();
  }

  return document.fonts.ready.then(() => undefined);
};

const checkAnimationsComplete = (): Promise<void> => {
  return new Promise(resolve => {
    if (!document.getAnimations) {
      resolve();
      return;
    }

    const animations = document.getAnimations();

    if (animations.length === 0) {
      resolve();
      return;
    }

    const animationPromises = animations.map(animation =>
      animation.finished.catch(() => undefined)
    );

    const timeoutPromise = new Promise<void>(timeoutResolve => {
      setTimeout(timeoutResolve, ANIMATION_TIMEOUT_MS);
    });

    Promise.race([Promise.all(animationPromises), timeoutPromise]).then(() => {
      resolve();
    });
  });
};
